import React, { useEffect, useState } from 'react';
import { Box, Button, Group, Textarea, Input, IconButton } from '@chakra-ui/react';
import { Formik, useFormik } from 'formik';
import moment from 'moment';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import api from '../../../api';
import DatePicker from '../../core/DatePicker';
import Constants from '../../../Constants';
import { actions } from '../../../reducers/reducer_establishment_details';
import { Tooltip } from '@/components/ui/tooltip';
import { IoInformationCircleOutline } from 'react-icons/io5';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '@/components/ui/dialog';
import { Alert } from '@/components/ui/alert';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';

const { requestEstblishmentDetailsEditListedStatus } = actions;

const AddHistory = ({ isOpenPopup, setIsOpenPopup, setData, estId, editItem }) => {
  // const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const addEstablishmentHistory = async values => {
    const payload = JSON.parse(JSON.stringify(values));
    if (!payload.reinstated_date || payload.reinstated_date === 'Invalid date') {
      delete payload.reinstated_date;
    }

    try {
      setIsLoading(true);
      if (editItem) {
        const response = await api.patch(`/op-audit-reason/${editItem.id}`, payload);
        if (response.ok) {
          const json = await response.json();
          setData(prevState => prevState.map(item => (item.id === json.id ? json : item)));
          toaster.create({
            title: 'Record updated successfully',
            status: 'success',
          });
        }
      } else {
        const response = await api.post('/op-audit-reason', { ...payload, est_id: estId });
        if (response.ok) {
          const json = await response.json();
          setData(prevState => [...prevState, json]);
          toaster.create({
            title: 'Record added successfully',
            status: 'success',
          });
        }
      }
      const listedStatusResponse = await api.get(`/op-establishment-detail/${estId}`);
      if (listedStatusResponse.ok) {
        dispatch(requestEstblishmentDetailsEditListedStatus(await listedStatusResponse.json()));
      }
    } catch (error) {
      toaster.create({
        title: error.message,
        status: 'error',
      });
    } finally {
      setIsLoading(false);
      setIsOpenPopup(false);
    }
  };

  // const parseDateString = (value, originalValue) => {
  //   return originalValue ? moment(originalValue).startOf('day').toDate() : new Date();
  // };

  const { values, errors, touched, setFieldValue, handleChange, handleSubmit } = useFormik({
    initialValues: {
      delisted_date: '',
      audit_failure: '',
      audit_reason: '',
      reinstated_date: '',
    },
    onSubmit: () => {
      const parsedValues = {
        ...values,
        delisted_date: moment(values.delisted_date).format('YYYY-MM-DD'),
        reinstated_date: moment(values.reinstated_date).format('YYYY-MM-DD'),
      };
      addEstablishmentHistory(parsedValues);
    },
    validationSchema: Yup.object().shape({
      delisted_date: Yup.date().required('Delisted Date is Required'),
      audit_failure: Yup.string().max(100).required('Audit Failure is Required'),
      audit_reason: Yup.string().max(100).required('Reason is Required'),
      reinstated_date: Yup.date()
        // .required('Re-instated Date is Required')
        .when('delisted_date', (delisted_date, schema) => {
          return delisted_date
            ? schema.min(delisted_date, 'Reinstasted date must be greater than Delisted date')
            : schema;
        }),
    }),
  });

  useEffect(() => {
    if (editItem) {
      setFieldValue('delisted_date', moment(editItem.delisted_date, Constants.DEFAULT_READ_DATE_FORMAT));
      setFieldValue('audit_failure', editItem.audit_failure);
      setFieldValue('audit_reason', editItem.audit_reason);
      setFieldValue('reinstated_date', moment(editItem.reinstated_date, Constants.DEFAULT_READ_DATE_FORMAT));
    }
  }, [editItem]);

  return (
    <DialogRoot open={isOpenPopup} onClose={() => setIsOpenPopup(false)} closeOnOverlayClick={false}>
      <DialogContent maxW="min(700px, 80%)">
        <DialogHeader borderBottomWidth="1px" fontSize="20px">
          {editItem ? 'Edit' : 'Add'}
        </DialogHeader>
        <DialogCloseTrigger asChild onClick={() => setIsOpenPopup(false)} />
        <Formik>
          <form onSubmit={handleSubmit}>
            <DialogBody padding="22px">
              <Box flexGrow={1} marginBottom="25px">
                <label>
                  Delisted Date&nbsp;
                  <Tooltip
                    content="Select the date when the document was officially removed from the list."
                    positioning={{ placement: 'right-end' }}
                  >
                    <IconButton
                      width="14px"
                      height="14px"
                      padding="0"
                      minW="auto"
                      borderRadius="50%"
                      color="#878787"
                      variant="unstyled"
                    >
                      <IoInformationCircleOutline size="14px" />
                    </IconButton>
                  </Tooltip>
                </label>
                <DatePicker
                  id="delisted_date"
                  value={moment(values.delisted_date, Constants.DEFAULT_READ_DATE_FORMAT)}
                  onChange={e => {
                    setFieldValue('delisted_date', e);
                  }}
                />
                {errors.delisted_date && touched.delisted_date && <Alert status="error" title={errors.delisted_date} />}
              </Box>
              <Box flexGrow={1} marginBottom="25px">
                <Field
                  label={
                    <>
                      Audit Failure&nbsp;
                      <Tooltip content="Provide details of the audit failure." positioning={{ placement: 'right-end' }}>
                        <IconButton
                          width="14px"
                          height="14px"
                          padding="0"
                          minW="auto"
                          borderRadius="50%"
                          color="#878787"
                          variant="unstyled"
                        >
                          <IoInformationCircleOutline size="14px" />
                        </IconButton>
                      </Tooltip>
                    </>
                  }
                  invalid={errors.audit_failure && touched.audit_failure}
                  errorText={errors.audit_failure}
                >
                  <Input name="audit_failure" value={values.audit_failure} onChange={handleChange} />
                </Field>
              </Box>
              <Box flexGrow={1} marginBottom="25px">
                <Field
                  label={
                    <>
                      Reason&nbsp;
                      <Tooltip
                        content="Provide the reason for the audit failure."
                        positioning={{ placement: 'right-end' }}
                      >
                        <IconButton
                          width="14px"
                          height="14px"
                          padding="0"
                          minW="auto"
                          borderRadius="50%"
                          color="#878787"
                          variant="unstyled"
                        >
                          <IoInformationCircleOutline size="14px" />
                        </IconButton>
                      </Tooltip>
                    </>
                  }
                  invalid={errors.audit_reason && touched.audit_reason}
                  errorText={errors.audit_reason}
                >
                  <Textarea height="120px" value={values.audit_reason} onChange={handleChange} name="audit_reason" />
                </Field>
              </Box>
              <Box flexGrow={1}>
                <label>
                  Re-instated Date&nbsp;
                  <Tooltip
                    content="Select the date when the document was re-instated to the list"
                    positioning={{ placement: 'right-end' }}
                  >
                    <IconButton
                      width="14px"
                      height="14px"
                      padding="0"
                      minW="auto"
                      borderRadius="50%"
                      color="#878787"
                      variant="unstyled"
                    >
                      <IoInformationCircleOutline size="14px" />
                    </IconButton>
                  </Tooltip>
                </label>
                <DatePicker
                  id="reinstated_date"
                  value={moment(values.reinstated_date, Constants.DEFAULT_READ_DATE_FORMAT)}
                  onChange={e => {
                    setFieldValue('reinstated_date', e);
                  }}
                />
                {errors.reinstated_date && touched.reinstated_date && (
                  <Alert status="error" title={errors.reinstated_date} />
                )}
              </Box>
            </DialogBody>
            <DialogFooter borderTopWidth="1px" marginTop="26px" padding="16px 22px">
              <Group marginLeft="auto">
                <Button type="button" fontSize="16px" width="126px" height="43px" backgroundColor="gray.200" color="#333" onClick={() => setIsOpenPopup(false)}>
                  Close
                </Button>
                &nbsp;
                <Button
                  type="submit"
                  width="126px"
                  height="43px"
                  colorScheme="actionPrimary"
                  marginLeft="10px"
                  fontSize="16px"
                  loadingText="Saving..."
                  loading={isLoading}
                >
                  Save
                </Button>
              </Group>
            </DialogFooter>
          </form>
        </Formik>
      </DialogContent>
    </DialogRoot>
  );
};

AddHistory.propTypes = {
  isOpenPopup: PropTypes.bool,
  setIsOpenPopup: PropTypes.func,
  setData: PropTypes.func,
  estId: PropTypes.number,
  editItem: PropTypes.shape({}) || null,
};
export default AddHistory;
